import { useCallback, useEffect, useState } from 'react';
import {
  executionApi,
  money,
  pct,
  type TransferExecution,
  type WithdrawalAddress,
} from '../lib/api.ts';

/**
 * Live transfer executions, and the deposit addresses they depend on.
 *
 * The ordering on this screen is deliberate: anything the owner is currently
 * holding sits at the top, above everything else, whether it is one minute old
 * or one day. A transfer that stopped halfway is the only thing here that
 * costs money by being ignored, and it should not require scrolling.
 */

/** The six steps of a healthy round trip, in order. */
const STEPS = ['BUYING', 'BOUGHT', 'WITHDRAWING', 'IN_TRANSIT', 'ARRIVED', 'SELLING'] as const;

const STEP_LABEL: Record<string, string> = {
  BUYING: 'buying',
  BOUGHT: 'bought',
  WITHDRAWING: 'sending',
  IN_TRANSIT: 'in transit',
  ARRIVED: 'arrived',
  SELLING: 'selling',
};

const TERMINAL_CLASS: Record<string, string> = {
  COMPLETED: 'pill ok',
  FAILED: 'pill',
  FAILED_HOLDING: 'pill bad',
  TIMED_OUT_HOLDING: 'pill bad',
  BLOCKED_HOLDING: 'pill bad',
  HELD_BELOW_ABORT: 'pill warn',
};

function isTerminal(state: string): boolean {
  return state in TERMINAL_CLASS;
}

/** How far along a run is, for the progress strip. */
function stepIndex(state: string): number {
  const i = STEPS.indexOf(state as (typeof STEPS)[number]);
  if (i >= 0) return i;
  if (state === 'COMPLETED') return STEPS.length;
  return -1;
}

function Progress({ execution }: { execution: TransferExecution }) {
  const reached = stepIndex(execution.state);
  const failed = execution.state.startsWith('FAILED') || execution.state.startsWith('TIMED_OUT');

  return (
    <div className="steps">
      {STEPS.map((step, i) => {
        const done = reached > i || execution.state === 'COMPLETED';
        const current = execution.state === step;
        const cls = current ? (failed ? 'step bad' : 'step current') : done ? 'step done' : 'step';
        return (
          <span key={step} className={cls} title={STEP_LABEL[step]}>
            {STEP_LABEL[step]}
          </span>
        );
      })}
    </div>
  );
}

function Row({ e, quoteCurrency }: { e: TransferExecution; quoteCurrency: string }) {
  const pnl = e.netPnl === null ? null : Number(e.netPnl);
  return (
    <div className={e.holdingCoin && isTerminal(e.state) ? 'card danger' : 'card'}>
      <div className="card-head">
        <strong>
          {e.coin} · {e.buyExchange} → {e.sellExchange}
        </strong>
        <span className={TERMINAL_CLASS[e.state] ?? 'pill warn'}>{e.state}</span>
      </div>

      <Progress execution={e} />

      <div className="grid">
        <span>size</span>
        <span>
          {money(Number(e.sizeQuote))} {quoteCurrency}
        </span>
        <span>expected</span>
        <span>{pct(e.expectedNetPct)}</span>
        {e.baseBought && (
          <>
            <span>bought</span>
            <span>
              {e.baseBought} {e.coin} @ {e.buyAvgPrice}
            </span>
          </>
        )}
        {e.withdrawalId && (
          <>
            <span>withdrawal</span>
            <span className="mono">{e.withdrawalId}</span>
          </>
        )}
        {e.amountReceived && (
          <>
            <span>received</span>
            <span>
              {e.amountReceived} {e.coin}
            </span>
          </>
        )}
        {e.realizedNetPct !== null && (
          <>
            <span>net on arrival</span>
            <span>{pct(e.realizedNetPct)}</span>
          </>
        )}
        {pnl !== null && (
          <>
            <span>P&amp;L</span>
            <span className={pnl >= 0 ? 'ok' : 'bad'}>
              {pnl >= 0 ? '+' : ''}
              {money(pnl)} {quoteCurrency}
            </span>
          </>
        )}
      </div>

      {e.failureReason && <p className="reason">{e.failureReason}</p>}

      {e.holdingCoin && isTerminal(e.state) && (
        <p className="reason">
          This run stopped while holding {e.coin}. Nothing further happens automatically.
        </p>
      )}
    </div>
  );
}

export function Executions({ quoteCurrency }: { quoteCurrency: string }) {
  const [executions, setExecutions] = useState<TransferExecution[]>([]);
  const [holding, setHolding] = useState<TransferExecution[]>([]);
  const [addresses, setAddresses] = useState<WithdrawalAddress[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [runs, addr] = await Promise.all([executionApi.list(), executionApi.addresses()]);
      setExecutions(runs.executions);
      setHolding(runs.holding);
      setAddresses(addr.addresses);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
    // A transfer changes state on the order of seconds to minutes, so polling
    // is honest here — a websocket would be more machinery for the same answer.
    const timer = setInterval(() => void load(), 4000);
    return () => clearInterval(timer);
  }, [load]);

  const others = executions.filter((e) => !holding.some((h) => h.id === e.id));

  return (
    <section>
      <header className="page-head">
        <div>
          <h2>Transfers</h2>
          <p className="sub">
            Buy, send, sell. The owner is exposed to the coin from the moment the buy fills until
            it sells.
          </p>
        </div>
      </header>

      {error && <p className="error">{error}</p>}

      {holding.length > 0 && (
        <>
          <h3 className="section-title bad">Holding — needs a decision</h3>
          {holding.map((e) => (
            <Row key={e.id} e={e} quoteCurrency={quoteCurrency} />
          ))}
        </>
      )}

      <h3 className="section-title">
        Recent {others.length > 0 ? `(${others.length})` : ''}
      </h3>
      {others.length === 0 && (
        <p className="empty">
          No transfers yet. Execute one from the opportunities screen — a dry run walks the whole
          flow against live prices without moving money.
        </p>
      )}
      {others.map((e) => (
        <Row key={e.id} e={e} quoteCurrency={quoteCurrency} />
      ))}

      <h3 className="section-title">Deposit addresses</h3>
      <p className="sub">
        A transfer can only send to an address stored here and marked whitelisted. Whitelisting
        happens on the sending venue, not here — this records that you have done it. Without it a
        withdrawal is rejected <em>after</em> the buy has filled.
      </p>
      {addresses.length === 0 && (
        <p className="empty">None stored. Transfers cannot execute until at least one exists.</p>
      )}
      {addresses.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Receiving venue</th>
              <th>Coin</th>
              <th>Network</th>
              <th>Address</th>
              <th>Whitelisted</th>
            </tr>
          </thead>
          <tbody>
            {addresses.map((a) => (
              <tr key={a.id}>
                <td>{a.exchange}</td>
                <td>{a.coin}</td>
                <td>{a.network}</td>
                <td className="mono">{a.address}</td>
                <td>
                  <span className={a.whitelisted ? 'pill ok' : 'pill bad'}>
                    {a.whitelisted ? 'yes' : 'not confirmed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
